import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarRange } from "lucide-react";
import {
  addDays, addMonths, addWeeks, endOfMonth, endOfWeek, format, isSameDay, isSameMonth,
  startOfDay, endOfDay, startOfMonth, startOfWeek,
} from "date-fns";
import { pt } from "date-fns/locale";
import { toast } from "sonner";
import { api, formatError } from "../lib/api.js";
import { WORK_TYPE_COLORS, WORK_TYPE_UNDEFINED } from "../lib/bps.js";

const WEEK_OPTS = { weekStartsOn: 1 };

function rangeFor(view, cursor) {
  if (view === "week") return [startOfWeek(cursor, WEEK_OPTS), endOfWeek(cursor, WEEK_OPTS)];
  return [startOfWeek(startOfMonth(cursor), WEEK_OPTS), endOfWeek(endOfMonth(cursor), WEEK_OPTS)];
}

const EventChip = ({ ev, day, onOpen }) => {
  const entry = new Date(ev.scheduled_entry_at);
  const exit = new Date(ev.expected_exit_at);
  const note = isSameDay(entry, day) ? `Entra ${format(entry, "HH:mm")}` : isSameDay(exit, day) ? `Sai ${format(exit, "HH:mm")}` : "Na oficina";
  return (
    <button onClick={() => onOpen(ev.id)} title={`${ev.number} · ${ev.customer_name} — arraste para mudar a previsão de saída`} data-testid={`bps-cal-event-${ev.id}`}
      draggable onDragStart={(e) => { e.dataTransfer.setData("text/plain", ev.id); e.dataTransfer.effectAllowed = "move"; }}
      className={`w-full text-left text-[11px] leading-tight border rounded-sm px-1.5 py-1 hover:brightness-95 transition-[filter] ${WORK_TYPE_COLORS[ev.work_type] || WORK_TYPE_UNDEFINED}`}>
      <div className="font-mono font-semibold truncate">{ev.vehicle_registration}</div>
      <div className="truncate opacity-90">{ev.brand} {ev.model}</div>
      <div className="opacity-75">{note}</div>
    </button>
  );
};

export default function BodyShopCalendarPage() {
  const nav = useNavigate();
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [dragOver, setDragOver] = useState(null);
  const [reload, setReload] = useState(0);
  const [start, end] = useMemo(() => rangeFor(view, cursor), [view, cursor]);

  useEffect(() => { api.get("/body-shop-cases/meta/enums").then((r) => setWorkTypes(r.data.work_types || [])); }, []);
  useEffect(() => {
    api.get("/body-shop-cases/calendar/events", { params: { start: start.toISOString(), end: endOfDay(end).toISOString() } })
      .then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [start, end, reload]);

  // Drag & drop moves only the expected exit to the target day, keeping its time.
  const onDropDay = async (e, day) => {
    e.preventDefault(); setDragOver(null);
    const ev = events.find((x) => x.id === e.dataTransfer.getData("text/plain"));
    if (!ev) return;
    const oldExit = new Date(ev.expected_exit_at);
    const newExit = new Date(day);
    newExit.setHours(oldExit.getHours(), oldExit.getMinutes(), 0, 0);
    if (isSameDay(newExit, oldExit)) return;
    try {
      await api.put(`/body-shop-cases/${ev.id}/schedule`, { scheduled_entry_at: ev.scheduled_entry_at, expected_exit_at: newExit.toISOString(), comment: "Reagendado no calendário" });
      toast.success(`${ev.vehicle_registration}: saída prevista para ${format(newExit, "d MMM HH:mm", { locale: pt })}`);
      setReload((n) => n + 1);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail, "Não foi possível reagendar"));
    }
  };

  const days = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
  const eventsOn = (day) => events.filter((ev) => new Date(ev.scheduled_entry_at) <= endOfDay(day) && new Date(ev.expected_exit_at) >= startOfDay(day));
  const move = (n) => setCursor((c) => (view === "week" ? addWeeks(c, n) : addMonths(c, n)));
  const title = view === "week" ? `${format(start, "d MMM", { locale: pt })} – ${format(end, "d MMM yyyy", { locale: pt })}` : format(cursor, "MMMM yyyy", { locale: pt });

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="bps-calendar-page">
      <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
        <Link to="/body-shop-cases" className="flex items-center gap-1 hover:text-[#1F3864]"><ChevronLeft className="w-3 h-3" /> Bate-chapas</Link>
        <span>/</span><span>Calendário</span>
      </div>
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2"><CalendarRange className="w-6 h-6 text-[#1F3864]" /> Calendário BPS</h1>
        <div className="flex items-center gap-2">
          <div className="flex border border-gray-300 rounded-sm overflow-hidden">
            {[["week", "Semana"], ["month", "Mês"]].map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`bps-cal-view-${k}`} className={`px-3 py-1.5 text-sm transition-colors ${view === k ? "bg-[#1F3864] text-white" : "bg-white hover:bg-[#F4F6F8]"}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => move(-1)} className="p-1.5 border border-gray-300 rounded-sm hover:bg-[#F4F6F8]" data-testid="bps-cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCursor(new Date())} className="px-3 py-1.5 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]" data-testid="bps-cal-today">Hoje</button>
          <button onClick={() => move(1)} className="p-1.5 border border-gray-300 rounded-sm hover:bg-[#F4F6F8]" data-testid="bps-cal-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="font-heading font-semibold capitalize text-[#1A1A2E]" data-testid="bps-cal-title">{title}</div>
          <div className="text-xs text-[#6C757D]">Arraste uma viatura para outro dia para mudar a previsão de saída.</div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {workTypes.map((t) => <span key={t.key} className={`text-[10px] font-semibold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${WORK_TYPE_COLORS[t.key]}`}>{t.label}</span>)}
        </div>
      </div>
      <div className="grid grid-cols-7 border-l border-t border-gray-200 bg-white">
        {days.slice(0, 7).map((d) => <div key={`h${d}`} className="border-r border-b border-gray-200 px-2 py-1.5 text-[10px] uppercase tracking-widest text-[#6C757D] bg-[#F4F6F8]">{format(d, "EEE", { locale: pt })}</div>)}
        {days.map((d) => {
          const list = eventsOn(d);
          const muted = view === "month" && !isSameMonth(d, cursor);
          return (
            <div key={d.toISOString()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(d.toISOString()); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => onDropDay(e, d)}
              className={`border-r border-b border-gray-200 p-1.5 transition-colors ${view === "week" ? "min-h-[420px]" : "min-h-[120px]"} ${muted ? "bg-[#FAFBFC] opacity-60" : ""} ${dragOver === d.toISOString() ? "bg-[#EEF2F8] ring-2 ring-inset ring-[#1F3864]/40" : ""}`} data-testid={`bps-cal-day-${format(d, "yyyy-MM-dd")}`}>
              <div className={`text-xs font-semibold mb-1 ${isSameDay(d, new Date()) ? "text-white bg-[#1F3864] rounded-sm inline-block px-1.5" : "text-[#1A1A2E]"}`}>{format(d, "d")}</div>
              <div className="space-y-1">{list.map((ev) => <EventChip key={ev.id} ev={ev} day={d} onOpen={(id) => nav(`/body-shop-cases/${id}`)} />)}</div>
            </div>
          );
        })}
      </div>
      {events.length === 0 && <div className="text-sm text-[#6C757D] italic mt-3" data-testid="bps-cal-empty">Sem viaturas agendadas neste período.</div>}
    </div>
  );
}
